import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGS } from "../../i18n";
import "./LanguageSwitcher.css";

export default function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Resolve current lang to a known short code; default to first.
  const current =
    SUPPORTED_LANGS.find((l) => l.code === i18n.resolvedLanguage) ||
    SUPPORTED_LANGS.find((l) => i18n.resolvedLanguage?.startsWith(l.code)) ||
    SUPPORTED_LANGS[0];

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const change = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div className="lang-switch" ref={ref}>
      <button
        type="button"
        className="lang-switch__btn"
        aria-label={t("lang_switcher.aria")}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="lang-switch__short">{current.short}</span>
        <svg
          className={`lang-switch__chev${open ? " lang-switch__chev--open" : ""}`}
          width="10"
          height="6"
          viewBox="0 0 10 6"
          aria-hidden="true"
        >
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <ul className="lang-switch__menu" role="listbox">
          {SUPPORTED_LANGS.map((l) => (
            <li key={l.code}>
              <button
                type="button"
                role="option"
                aria-selected={l.code === current.code}
                className={`lang-switch__item${l.code === current.code ? " lang-switch__item--active" : ""}`}
                onClick={() => change(l.code)}
              >
                <span className="lang-switch__item-short">{l.short}</span>
                <span className="lang-switch__item-label">{l.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
